/**
 * coconnect — TruckMap
 * Actual travelled path = authoritative route_history + durable local driver_gps_events.
 * Planned path = road-network geometry. Direct distance is never drawn as a route.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { apiClient } from '../services/api';
import { ESRI_TILE_URL, ESRI_TILE_OPTIONS, OSM_TILE_URL, OSM_TILE_OPTIONS } from '../services/googleMaps';

const truckIcon = L.divIcon({
  html: `<div class="truck-map-vehicle-marker" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 16-7-4-7 4 7-16z"/></svg></div>`,
  className: 'truck-map-div-icon', iconSize: [34, 34], iconAnchor: [17, 17],
});
const destinationIcon = L.divIcon({
  html: `<div class="truck-map-destination-marker" aria-hidden="true"><span></span></div>`,
  className: 'truck-map-div-icon', iconSize: [28, 28], iconAnchor: [14, 14],
});

type Point = [number, number];
export interface TruckRouteSummary {
  dispatchId: string;
  mode: 'road' | 'direct';
  distanceKm: number;
  durationSeconds: number | null;
  source: string;
}
interface PlannedRoute { geometry: Point[]; distanceKm: number; durationSeconds: number; source: string; }
interface DispatchItem {
  id: string; batch_gps_lat: number | null; batch_gps_lng: number | null;
  batch_farmer_name: string; batch_farmer_village: string;
  batch_est_weight?: number | null; status: string;
}
interface TruckMapProps {
  truckLat: number | string | null; truckLng: number | string | null;
  liveLat?: number | null; liveLng?: number | null; gpsActive?: boolean; gpsAccuracy?: number | null;
  destinationLat?: number | null; destinationLng?: number | null; dispatches?: DispatchItem[];
  height?: string; onRouteSummaries?: (summaries: Record<string, TruckRouteSummary>) => void;
}

function validPoint(lat: unknown, lng: unknown): Point | null {
  const a = Number(lat), b = Number(lng);
  return Number.isFinite(a) && Number.isFinite(b) && a >= -90 && a <= 90 && b >= -180 && b <= 180 ? [a, b] : null;
}
function haversine(lat1:number,lng1:number,lat2:number,lng2:number) {
  const R=6371, r=(d:number)=>d*Math.PI/180, dLat=r(lat2-lat1), dLng=r(lng2-lng1);
  const a=Math.sin(dLat/2)**2+Math.cos(r(lat1))*Math.cos(r(lat2))*Math.sin(dLng/2)**2;
  return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
}
async function localGpsEvents(dispatchId:string):Promise<any[]> {
  if (typeof indexedDB === 'undefined') return [];
  return new Promise(resolve => {
    const open=indexedDB.open('coconnect-offline');
    open.onerror=()=>resolve([]); open.onblocked=()=>resolve([]);
    open.onsuccess=()=>{
      const db=open.result;
      try {
        if(!db.objectStoreNames.contains('driver_gps_events')){db.close();resolve([]);return;}
        const req=db.transaction('driver_gps_events','readonly').objectStore('driver_gps_events').getAll();
        req.onerror=()=>{try{db.close();}catch{} resolve([]);};
        req.onsuccess=()=>{
          const rows=(Array.isArray(req.result)?req.result:[])
            .filter((x:any)=>x.dispatchId===dispatchId && validPoint(x.lat,x.lng))
            .sort((a:any,b:any)=>Number(a.sequence_no||0)-Number(b.sequence_no||0));
          try{db.close();}catch{} resolve(rows);
        };
      } catch { try{db.close();}catch{} resolve([]); }
    };
  });
}
type TrailSample = {
  p: Point;
  t: number;
  s: number;
  accuracyM: number | null;
  speedKmh: number | null;
  odometerKm: number | null;
};

function mergeTrail(serverRows:any[], localRows:any[]):TrailSample[] {
  const m=new Map<string,TrailSample>();
  const add=(x:any,local:boolean)=>{
    const p=validPoint(x.lat,x.lng); if(!p)return;
    const raw=x.observed_at??x.timestamp??x.recorded_at??x.created_at;
    const parsed=raw?Date.parse(String(raw)):NaN;
    const t=Number.isFinite(parsed)?parsed:0, s=Number(x.sequence_no||0);
    const rawAccuracy=x.accuracy_m??x.accuracyM;
    const rawSpeed=x.speed_kmh??x.speedKmh;
    const rawOdometer=x.odometer_km??x.odometerKm;
    const accuracyM=Number.isFinite(Number(rawAccuracy))&&Number(rawAccuracy)>0?Number(rawAccuracy):null;
    const speedKmh=Number.isFinite(Number(rawSpeed))&&Number(rawSpeed)>=0?Number(rawSpeed):null;
    const odometerKm=Number.isFinite(Number(rawOdometer))&&Number(rawOdometer)>=0?Number(rawOdometer):null;
    const key=String(x.event_id||'')||`${local?'L':'S'}:${s}:${t}:${p[0].toFixed(7)}:${p[1].toFixed(7)}`;
    m.set(key,{p,t,s,accuracyM,speedKmh,odometerKm});
  };
  serverRows.forEach(x=>add(x,false)); localRows.forEach(x=>add(x,true));
  return [...m.values()].sort((a,b)=>(a.t&&b.t&&a.t!==b.t)?a.t-b.t:a.s-b.s);
}

function trailDistanceMeters(a:Point,b:Point):number {
  return haversine(a[0],a[1],b[0],b[1])*1000;
}

/**
 * Presentation-only GPS jitter suppression.
 * Raw route_history and IndexedDB GPS evidence remain untouched.
 *
 * A point is rendered only when displacement from the last accepted display
 * point is meaningfully larger than the uncertainty of the two fixes.
 * Higher reported speed lowers the minimum threshold, while stationary/unknown
 * speed requires stronger displacement evidence.
 */
function filterDisplayTrail(samples:TrailSample[]):Point[] {
  if(samples.length===0)return [];
  if(samples.length===1)return [samples[0].p];

  const accepted:TrailSample[]=[samples[0]];

  for(let i=1;i<samples.length;i++){
    const current=samples[i];
    const previous=accepted[accepted.length-1];
    const displacementM=trailDistanceMeters(previous.p,current.p);

    const prevAcc=previous.accuracyM??15;
    const currAcc=current.accuracyM??15;
    const uncertainty=Math.max(prevAcc,currAcc);

    // Unknown/stationary fixes must clear a larger uncertainty-derived gate.
    // Clear moving fixes can use a lower floor while still rejecting micro-jitter.
    const moving=(current.speedKmh??0)>=2;
    const thresholdM=moving
      ? Math.max(5,Math.min(15,uncertainty*0.45))
      : Math.max(8,Math.min(30,uncertainty*0.90));

    if(displacementM>=thresholdM){
      accepted.push(current);
    }
  }

  return accepted.map(x=>x.p);
}

const TruckMap:React.FC<TruckMapProps>=({
  truckLat,truckLng,liveLat,liveLng,gpsActive=false,gpsAccuracy=null,
  destinationLat:_destinationLat,destinationLng:_destinationLng,dispatches=[],height='500px',onRouteSummaries,
})=>{
  const mapRef=useRef<L.Map|null>(null), containerRef=useRef<HTMLDivElement>(null), layersRef=useRef<L.Layer[]>([]), tileLayerRef=useRef<L.TileLayer|null>(null);
  const userMovedRef=useRef(false), fittedRef=useRef<string|null>(null);
  const [mapType,setMapType]=useState<'satellite'|'street'>('street');
  const [actualTrail,setActualTrail]=useState<TrailSample[]>([]);
  const [plannedRoutes,setPlannedRoutes]=useState<Record<string,PlannedRoute>>({});
  const [routeState,setRouteState]=useState<'idle'|'loading'|'road'|'fallback'>('idle');

  const effectiveLat=liveLat!=null?Number(liveLat):(gpsActive?null:(truckLat!=null?Number(truckLat):null));
  const effectiveLng=liveLng!=null?Number(liveLng):(gpsActive?null:(truckLng!=null?Number(truckLng):null));
  const primary=useMemo(()=>dispatches.find(d=>d.status==='IN_TRANSIT')||dispatches.find(d=>d.status==='DISPATCHED')||dispatches[0]||null,[dispatches]);
  const signature=useMemo(()=>dispatches.map(d=>[d.id,d.status,Number(d.batch_gps_lat||0).toFixed(5),Number(d.batch_gps_lng||0).toFixed(5)].join(':')).join('|'),[dispatches]);

  useEffect(()=>{
    if(!containerRef.current||mapRef.current)return;
    const start=validPoint(effectiveLat,effectiveLng)||[4.6333,9.45];
    const map=L.map(containerRef.current,{zoomControl:true}).setView(start,effectiveLat!=null?16:12);
    tileLayerRef.current=L.tileLayer(OSM_TILE_URL,{...OSM_TILE_OPTIONS,maxZoom:20}).addTo(map);
    mapRef.current=map; map.on('dragstart zoomstart',()=>{userMovedRef.current=true;});
    const fix=()=>{try{map.invalidateSize();}catch{}}; fix(); setTimeout(fix,200); setTimeout(fix,500);
    return()=>{map.remove();mapRef.current=null;layersRef.current=[];};
  },[]);
  useEffect(()=>{
    if(!mapRef.current||!tileLayerRef.current)return;
    mapRef.current.removeLayer(tileLayerRef.current);
    tileLayerRef.current=mapType==='satellite'
      ?L.tileLayer(ESRI_TILE_URL,{...ESRI_TILE_OPTIONS,maxNativeZoom:18,maxZoom:20}).addTo(mapRef.current)
      :L.tileLayer(OSM_TILE_URL,{...OSM_TILE_OPTIONS,maxZoom:20}).addTo(mapRef.current);
  },[mapType]);

  const refreshTrail=useCallback(async()=>{
    if(!primary?.id){setActualTrail([]);return;}
    const localPromise=localGpsEvents(primary.id); let serverRows:any[]=[];
    try{
      const res=await apiClient.get(`/truck-portal/route/${primary.id}`);
      serverRows=Array.isArray(res.data?.route)?res.data.route:[];
    }catch{}
    setActualTrail(mergeTrail(serverRows,await localPromise));
  },[primary?.id]);
  useEffect(()=>{void refreshTrail();const t=setInterval(()=>void refreshTrail(),10000);return()=>clearInterval(t);},[refreshTrail]);
  useEffect(()=>{if(gpsActive&&liveLat!=null&&liveLng!=null)void refreshTrail();},[gpsActive,liveLat,liveLng,refreshTrail]);

  useEffect(()=>{
    let cancelled=false; const origin=validPoint(effectiveLat,effectiveLng);
    if(!origin||!dispatches.length){setRouteState('idle');setPlannedRoutes({});return;}
    const timer=setTimeout(()=>{
      setRouteState('loading');
      void Promise.all(dispatches.map(async d=>{
        const dest=validPoint(d.batch_gps_lat,d.batch_gps_lng); if(!dest)return null;
        const direct=haversine(origin[0],origin[1],dest[0],dest[1]);
        try{
          const res=await apiClient.get('/truck-portal/road-route',{params:{fromLat:origin[0],fromLng:origin[1],toLat:dest[0],toLng:dest[1]}});
          const rr=res.data?.route;
          const geometry=(Array.isArray(rr?.geometry)?rr.geometry:[]).map((p:any)=>validPoint(p?.[0],p?.[1])).filter(Boolean) as Point[];
          if(geometry.length<2)throw new Error('no geometry');
          const planned:PlannedRoute={geometry,distanceKm:Number(rr.distance_km||direct),durationSeconds:Number(rr.duration_seconds||0),source:String(rr.source||'road-router')};
          return {id:d.id,planned,summary:{dispatchId:d.id,mode:'road' as const,distanceKm:planned.distanceKm,durationSeconds:planned.durationSeconds,source:planned.source}};
        }catch{
          return {id:d.id,planned:null,summary:{dispatchId:d.id,mode:'direct' as const,distanceKm:direct,durationSeconds:null,source:'direct-distance-fallback'}};
        }
      })).then(results=>{
        if(cancelled)return;
        const routes:Record<string,PlannedRoute>={}, summaries:Record<string,TruckRouteSummary>={};
        for(const r of results){if(!r)continue;summaries[r.id]=r.summary;if(r.planned)routes[r.id]=r.planned;}
        setPlannedRoutes(routes); onRouteSummaries?.(summaries); setRouteState(Object.keys(routes).length?'road':'fallback');
      });
    },700);
    return()=>{cancelled=true;clearTimeout(timer);};
  },[effectiveLat!=null?Number(effectiveLat).toFixed(4):'none',effectiveLng!=null?Number(effectiveLng).toFixed(4):'none',signature,onRouteSummaries]);

  useEffect(()=>{
    const map=mapRef.current;if(!map)return;
    layersRef.current.forEach(l=>{try{map.removeLayer(l);}catch{}});layersRef.current=[];
    const all:Point[]=[]; const origin=validPoint(effectiveLat,effectiveLng);
    const displayTrail=filterDisplayTrail(actualTrail);
    if(displayTrail.length>=2){
      const l=L.polyline(displayTrail,{color:'#16a34a',weight:5,opacity:.9,lineCap:'round',lineJoin:'round'}).addTo(map);
      l.bindTooltip(`Actual travelled GPS path · ${displayTrail.length} displayed of ${actualTrail.length} raw points`);
      layersRef.current.push(l);
      all.push(...displayTrail);
    }
    if(origin){
      all.push(origin);
      const m=L.marker(origin,{icon:truckIcon,zIndexOffset:1000}).addTo(map);
      m.bindPopup(`<div class="truck-map-popup"><strong>DRIVER POSITION</strong><span>${origin[0].toFixed(5)}, ${origin[1].toFixed(5)}</span><small>${liveLat!=null?'LIVE GPS':'LAST KNOWN POSITION'}</small></div>`);
      layersRef.current.push(m);
      layersRef.current.push(L.circle(origin,{radius:Math.max(12,Math.min(Number(gpsAccuracy||50),100)),color:'#F5A623',fillColor:'#F5A623',fillOpacity:.05,weight:1}).addTo(map));
    }
    dispatches.forEach((d,idx)=>{
      const dest=validPoint(d.batch_gps_lat,d.batch_gps_lng);if(!dest)return;all.push(dest);
      const m=L.marker(dest,{icon:destinationIcon,zIndexOffset:500}).addTo(map), planned=plannedRoutes[d.id];
      let popup=`<div class="truck-map-popup"><strong>COLLECTION POINT ${dispatches.length>1?`#${idx+1}`:''}</strong><span>${d.batch_farmer_name||'Farmer'}</span>`;
      if(d.batch_farmer_village)popup+=`<small>${d.batch_farmer_village}</small>`;
      popup+=`<small>${d.batch_est_weight??'N/A'} kg · ${d.status.replace(/_/g,' ')}</small>`;
      if(planned)popup+=`<span>${planned.distanceKm.toFixed(1)} km by road${planned.durationSeconds>0?` · ~${Math.max(1,Math.round(planned.durationSeconds/60))} min`:''}</span>`;
      else if(origin)popup+=`<small>Direct distance: ${haversine(origin[0],origin[1],dest[0],dest[1]).toFixed(1)} km · road route unavailable</small>`;
      m.bindPopup(popup+'</div>');layersRef.current.push(m);
      if(planned?.geometry?.length>=2){
        const r=L.polyline(planned.geometry,{color:idx===0?'#2563eb':'#7c3aed',weight:idx===0?4:3,opacity:idx===0?.86:.7,dashArray:idx===0?'10, 7':'7, 7',lineCap:'round',lineJoin:'round'}).addTo(map);
        r.bindTooltip(`Planned road route · ${planned.distanceKm.toFixed(1)} km`);layersRef.current.push(r);all.push(...planned.geometry);
      }
    });
    const fitKey=primary?.id||'none';
    if(!userMovedRef.current&&fittedRef.current!==fitKey&&all.length){
      fittedRef.current=fitKey;
      if(all.length>=2)map.fitBounds(L.latLngBounds(all),{paddingTopLeft:[34,82],paddingBottomRight:[34,92],maxZoom:16});
      else{const a=Number(gpsAccuracy||9999),z=a<=10?18:a<=30?17:a<=100?16:14;map.setView(all[0],z);}
    }
  },[effectiveLat,effectiveLng,actualTrail,plannedRoutes,signature,gpsAccuracy,primary?.id,liveLat]);

  const recenter=()=>{const map=mapRef.current,p=validPoint(effectiveLat,effectiveLng);if(!map||!p)return;userMovedRef.current=false;const a=Number(gpsAccuracy||9999),z=a<=10?18:a<=30?17:a<=100?16:14;map.flyTo(p,z,{duration:.45});};

  return <div className="truck-map-shell relative" style={{height}}>
    <div className="truck-map-controls" role="group" aria-label="Map view">
      <button type="button" aria-pressed={mapType==='street'} onClick={()=>setMapType('street')}>Street</button>
      <button type="button" aria-pressed={mapType==='satellite'} onClick={()=>setMapType('satellite')}>Satellite</button>
    </div>
    <button type="button" className="truck-map-recenter" onClick={recenter} disabled={effectiveLat==null||effectiveLng==null} aria-label="Recenter on driver position">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>
    </button>
    <div className={`truck-map-gps-state ${gpsActive?'is-active':''}`}><span className="truck-map-gps-dot"/><span>{gpsActive?(effectiveLat!=null?`GPS ${gpsAccuracy!=null?`±${Math.round(gpsAccuracy)}m`:'live'}`:'Acquiring GPS'):(effectiveLat!=null?'Last known position':'GPS off')}</span></div>
    <div className="absolute bottom-3 left-3 z-[800] rounded-lg border border-black/10 bg-white/90 dark:bg-ink-950/90 px-3 py-2 shadow-sm backdrop-blur text-[11px] leading-4 text-ink-700 dark:text-ink-200">
      <div className="font-semibold">Route evidence</div>
      <div>Raw GPS evidence: {actualTrail.length} point{actualTrail.length===1?'':'s'}</div>
      <div>Displayed trail: {filterDisplayTrail(actualTrail).length} point{filterDisplayTrail(actualTrail).length===1?'':'s'} · jitter-filtered view</div>
      <div>Planned route: {routeState==='loading'?'calculating…':routeState==='road'?'road network':routeState==='fallback'?'unavailable — direct distance only':'waiting'}</div>
    </div>
    {gpsActive&&effectiveLat==null&&<div className="absolute inset-0 z-[700] pointer-events-none flex items-center justify-center"><div className="rounded-lg bg-black/70 px-4 py-3 text-sm font-medium text-white shadow-lg">Acquiring accurate GPS fix…</div></div>}
    <div ref={containerRef} style={{height:'100%',width:'100%'}} className="truck-map-canvas bg-ink-100 dark:bg-ink-900"/>
  </div>;
};
export default TruckMap;
